// import React, { useState } from "react";
// import { Sidebar } from "./Sidebar";
// import { Footer } from "./Footer";
// import { useAuth } from "../contexts/AuthContext";
// import { NotificationBell } from "./NotificationsBell";
// import { SettingsDropdown } from "./SettingsDropdown";
// import { DirectMessageModal } from "./DirectMessage";
// import { Menu, X, MessageCircle } from "lucide-react";

// export default function Layout({ children }: { children: React.ReactNode }) {
//   const { logout } = useAuth();

//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const [isMinimized, setIsMinimized] = useState(false);
//   const [isMessageOpen, setIsMessageOpen] = useState(false);
//   const [unreadCount, setUnreadCount] = useState(0);

//    const SAFE_TOP = 32;
//   const SAFE_BOTTOM = 0;
//   const isMobile = false;

//   return (
//     <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">

//       {/* ✅ Desktop Sidebar */}
//       <div
//         className={`hidden md:flex transition-all duration-300 ${
//           isMinimized ? "w-20" : "w-64"
//         }`}
//       >
//         <Sidebar
//           isOpen={true}
//           onClose={() => {}}
//           isMinimized={isMinimized}
//           setIsMinimized={setIsMinimized}
//         />
//       </div>

//       {/* ✅ Mobile Sidebar */}
//       <div className="md:hidden">
//         <Sidebar
//           isOpen={sidebarOpen}
//           onClose={() => setSidebarOpen(false)}
//           isMinimized={false}
//           setIsMinimized={() => {}}
//         />
//       </div>

//       {/* ✅ Right side */}
//       <div className="flex flex-col flex-1 w-full">

//         {/* Header */}
//         <header className="flex justify-between items-center px-4 sm:px-6 py-3 border-b bg-white dark:bg-gray-800 shadow-sm">
//            style={{ marginTop: isMobile ? SAFE_TOP : 0 }}
//           <button
//             onClick={() => setSidebarOpen(!sidebarOpen)}
//             className="md:hidden"
//           >
//             {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
//           </button>

//           <div className="flex items-center gap-3 ml-auto">
//             <button
//               onClick={() => setIsMessageOpen(true)}
//               className="p-2 hover:bg-gray-100 rounded-lg"
//             >
//               <MessageCircle size={20} />
//             </button>

//             <NotificationBell onUnreadCountChange={setUnreadCount} />

//             <SettingsDropdown onLogout={logout} />
//           </div>
//         </header>

//         {/* Main */}
//         <main className="flex-1 overflow-auto">
//           <div className="w-full px-4 sm:px-6 lg:px-8 pb-16">
//             {children}
//           </div>
//         </main>

//         <Footer />
//       </div>

//       {/* Modal */}
//       <DirectMessageModal
//         isOpen={isMessageOpen}
//         onClose={() => setIsMessageOpen(false)}
//       />
//     </div>
//   );
// }

import React, { useState } from "react";
import { Sidebar } from "./Sidebar";
import { Footer } from "./Footer";
import { useAuth } from "../contexts/AuthContext";
import { NotificationBell } from "./NotificationsBell";
import { SettingsDropdown } from "./SettingsDropdown";
import { useAccess } from "../contexts/AccessContext";
import { DirectMessageModal } from "./DirectMessage";
import { useTheme } from "../hooks/useTheme";
import { useTitleAccent } from "../hooks/useTitleAccent";
import { useNotificationSocket } from "../hooks/useNotificationSocket";
import { Menu, X, MessageSquarePlus, NotebookText  } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AnnouncementTicker from './AnnouncementTicker';

export default function Layout({ children }: { children: React.ReactNode }) {
  useTheme();
  useTitleAccent();
  const { hasAccess, isPanelVisible } = useAccess();
  useNotificationSocket();
  const { logout, clockOut } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  // Notification popup toasts are intentionally disabled — new notifications
  // surface only in the bell (NotificationsBell), not as transient toasts.
  // (Previously useReminderStream popped a Sonner toast per notification.)

  const SAFE_TOP = 32;
  const SAFE_BOTTOM = 0;
  const isMobile = false;

  return (
    <div
      className="gt-themed flex h-screen overflow-hidden"
      style={{
        marginTop: isMobile ? SAFE_TOP : 0,
        background: 'var(--gt-surface-1)',
      }}
    >

      {/* ✅ Desktop Sidebar */}
      <div
        className={`hidden md:flex transition-all duration-300 ${isMinimized ? "w-20" : "w-64"
          }`}
      >
        <Sidebar
          isOpen={true}
          onClose={() => { }}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
        />
      </div>

      {/* ✅ Mobile Sidebar */}
      <div className="md:hidden">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isMinimized={false}
          setIsMinimized={() => { }}
        />
      </div>

      {/* ✅ Right side */}
      <div className="flex flex-col flex-1 min-w-0">

        {/* Header */}
        <header className="gt-topbar">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="gt-topbar-menu-btn md:hidden"
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <AnnouncementTicker />

          <div className="gt-topbar-actions">
            {(!hasAccess || isPanelVisible("todo")) && (
              <button
                onClick={() => navigate("/todo")}
                className="gt-topbar-action"
                title="Daily Planner"
                aria-label="Daily Planner"
              >
                <NotebookText size={18} />
              </button>
            )}

            <button
              onClick={() => setIsMessageOpen(true)}
              className="gt-topbar-action"
              title="New Message"
              aria-label="New Message"
            >
              <MessageSquarePlus size={18} />
            </button>

            <NotificationBell onUnreadCountChange={setUnreadCount} />

            <SettingsDropdown onLogout={logout} />

            {isMobile && (
              <button
                onClick={clockOut}
                className="gt-action-btn is-danger-outline"
                style={{ padding: '8px 14px', fontSize: 13 }}
              >
                Clock Out
              </button>
            )}
          </div>
        </header>

        {/* Main */}
        <main
          className="gt-themed flex-1 overflow-y-auto overflow-x-hidden"
          style={{
            paddingBottom: isMobile ? SAFE_BOTTOM : 0,
            background: 'var(--gt-surface-1)',
            color: 'var(--gt-text-primary)',
          }}
        >
          <div className="w-full max-w-full">
            {children}
          </div>
        </main>
      </div>

      {/* Modal */}
      <DirectMessageModal
        isOpen={isMessageOpen}
        onClose={() => setIsMessageOpen(false)}
      />
    </div>
  );
}
